import {
  Calendar,
  Card,
  CopyToClipboardButton,
  GenericStatusLabel,
  Icon,
  IconButton,
  Tooltip
} from '@hevoio/hevo-ui-toolkit';
import styles from './PeeringCard.module.scss';
import { ConnectivityBanner } from './ConnectivityBanner';
import { VPC_PEERING_CARD_DATA_IDS } from '../dataIds';
import { getInProgressLabel, getPeeringStatus } from '../utils/peeringStatus';
import {
  hasAction,
  VPCPeeringAction,
  VPCPeeringConnection,
  VPCPeeringFormMode
} from '../interface';

interface DetailField {
  key: string;
  label: string;
  value: string | null;
  /** Values the customer allow-lists or quotes to support are worth a copy button. */
  copyable?: boolean;
}

const getDetailFields = (connection: VPCPeeringConnection): DetailField[] => [
  { key: 'hevo_cidr', label: 'Hevo CIDR', value: connection.hevo_cidr, copyable: true },
  {
    key: 'hevo_tunnel_private_ip',
    label: 'Hevo Tunnel Private IP',
    value: connection.hevo_tunnel_private_ip,
    copyable: true
  },
  {
    key: 'hevo_tunnel_public_ip',
    label: 'Hevo Tunnel Public IP',
    value: connection.hevo_tunnel_public_ip,
    copyable: true
  },
  { key: 'peering_id', label: 'Peering ID', value: connection.peering_id, copyable: true },
  {
    key: 'cidr',
    label: 'VPC CIDR',
    value: connection.cidr?.join(', ') || null,
    copyable: true
  },
  {
    key: 'aws_account_id',
    label: 'AWS Account ID',
    value: connection.aws_account_id,
    copyable: true
  }
];

export interface PeeringCardProps {
  connection: VPCPeeringConnection;
  onEdit?: (connection: VPCPeeringConnection, mode: VPCPeeringFormMode) => void;
  onDelete?: (connection: VPCPeeringConnection) => void;
  onHowToAccept: (connection: VPCPeeringConnection) => void;
}

export function PeeringCard({ connection, onEdit, onDelete, onHowToAccept }: PeeringCardProps) {
  // Submitting a resubmit retries; submitting an edit only saves. Prefer
  // resubmit where the server grants it, because on a FAILED record an edit
  // alone is a dead end — it persists the correction and stays FAILED.
  const canResubmit = hasAction(connection, VPCPeeringAction.RESUBMIT);
  const canEdit = hasAction(connection, VPCPeeringAction.EDIT);
  const canOpenForm = canResubmit || canEdit;
  // The mode travels with the click: submitting must use whichever action the
  // server granted, so it cannot be re-derived from status further up.
  const formMode = canResubmit ? VPCPeeringFormMode.RESUBMIT : VPCPeeringFormMode.EDIT;
  const formLabel = canResubmit ? 'Edit & retry' : 'Edit';
  const canDelete = hasAction(connection, VPCPeeringAction.DELETE);
  const inProgressLabel = connection.change_in_progress
    ? getInProgressLabel(connection.status)
    : undefined;
  // status.description is extra information rather than a repeat of visible
  // text, so it is shown for every status.
  const statusDescription = connection.status?.description;

  return (
    <Card
      dataId={VPC_PEERING_CARD_DATA_IDS.card(connection.id)}
      className={styles.card}
      withBorder
    >
      <div className={styles.header}>
        <div className={styles.identity}>
          <Tooltip
            dataId={VPC_PEERING_CARD_DATA_IDS.name(connection.id)}
            title={connection.name ?? ''}
            disabled={!connection.name}
            enableOnTextOverflow
          >
            <p className={styles.name}>{connection.name}</p>
          </Tooltip>
          <p className={styles.meta}>
            <span>{connection.vpc_id}</span>
            <span className={styles.metaSeparator}>·</span>
            <span>{connection.aws_region}</span>
            <span className={styles.metaSeparator}>·</span>
            <Calendar value={connection.created_at} />
          </p>
        </div>

        <div className={styles.headerActions}>
          {inProgressLabel && (
            <span
              className={styles.inProgressChip}
              data-id={VPC_PEERING_CARD_DATA_IDS.changeInProgress(connection.id)}
            >
              <Icon name='refresh' size={1} className={styles.inProgressIcon} />
              {inProgressLabel}
            </span>
          )}

          <Tooltip
            dataId={VPC_PEERING_CARD_DATA_IDS.statusTooltip(connection.id)}
            title={statusDescription ?? ''}
            disabled={!statusDescription}
          >
            <span>
              <GenericStatusLabel status={getPeeringStatus(connection.status)} withIcon />
            </span>
          </Tooltip>

          {canOpenForm && (
            <IconButton
              dataId={VPC_PEERING_CARD_DATA_IDS.edit(connection.id)}
              label={formLabel}
              onClick={() => onEdit?.(connection, formMode)}
            >
              <Icon name='edit' />
            </IconButton>
          )}

          {canDelete && (
            <IconButton
              dataId={VPC_PEERING_CARD_DATA_IDS.delete(connection.id)}
              label='Delete'
              onClick={() => onDelete?.(connection)}
            >
              <Icon name='delete' />
            </IconButton>
          )}
        </div>
      </div>

      <ConnectivityBanner connection={connection} onHowToAccept={onHowToAccept} />

      <dl className={styles.details}>
        {getDetailFields(connection).map(({ key, label, value, copyable }) => (
          <div className={styles.detail} key={key}>
            <dt className={styles.detailLabel}>{label}</dt>
            <dd className={styles.detailValue}>
              <Tooltip
                dataId={VPC_PEERING_CARD_DATA_IDS.detailValue(connection.id, key)}
                title={value ?? ''}
                disabled={!value}
                enableOnTextOverflow
              >
                <span className={styles.detailText}>{value ?? '—'}</span>
              </Tooltip>
              {copyable && value && (
                <CopyToClipboardButton
                  dataId={VPC_PEERING_CARD_DATA_IDS.copy(connection.id, key)}
                  copyText={value}
                  toastMessage={`${label} copied`}
                  className={styles.detailCopy}
                  size='sm'
                  iconOnly
                  withoutBorder
                />
              )}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
